import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';

import { useSubscription } from '../../SubscriptionProvider';
import { ROUTE_HOME, isSubscriptionDevSuccessPreview } from '../../../../config/routes';
import { SUBSCRIPTION_STEPS } from '../../../../utils/subscriptions';
import {
  SUBSCRIPTION_TRACKER_TYPES,
  TRACKER_IDS,
  getTrackerIdEventName,
} from '../../../../config/tracker';
import { getOrderCheckoutSession, getSubscriptionPaymentIntentStatus } from '../../../../utils/service';
import { STRIPE_CHECKOUT_SESSION_STATUS } from '../../../../config/stripe';
import IconUnplug from '../../../../images/icon-unplug.svg';
import { Button, BUTTON_TYPE, BUTTON_SIZE } from '../../../../global/Button';
import GeneralLottie from '../../../../global/GeneralLottie';
import { CONTACT_US_URL, TRASHIE_APP_URL } from '../../../../config/externalURL';
import { useFirebaseAuth } from '../../../../../global/FirebaseProvider/FirebaseProvider';
import trashieLogo from '../../../../images/trashie-logo.webp';
import processingPayment from '../../../../animations/processing-payment.json';
import { trackMagellanPurchase } from '../../../../scripts/magellanPixel';
import { ARTSAI_PIXEL_ID } from '../../../../config/config';
import { pushSubscriptionPurchaseEvent } from '../../../../utils/gtm';
import {
  buildArtsAiPixelUrl,
  buildMagellanPurchaseMetadata,
  getDiscountCodeFromApplications,
} from '../../../../utils/trackingPixels';

import './SubscriptionConfirmation.scss';

const SUBSCRIPTION_CHECK_TIMEOUT = 1500;
const SUBSCRIPTION_CHECK_TRIES = 10;

const {
  subscription: {
    confirmation: {
      [SUBSCRIPTION_TRACKER_TYPES.VIEW]: trackerView,
      [SUBSCRIPTION_TRACKER_TYPES.CLOSE]: trackerClose,
    },
  },
} = TRACKER_IDS;

/* Figma 6370:10919 / 6370:11399 — checkout success “What happens next” */
const SUCCESS_NEXT_STEPS = [
  'Starter Kit ships in 3-5 business days.',
  'Fill your bags, drop them earn TrashieCash.',
  'Cash out for gift cards at any time.',
];

const SubscriptionConfirmation = () => {
  const confirmationScrollTargetRef = useRef(null);
  const verificationTimeoutRef = useRef(null);
  const {
    isLoading: isLoadingFirebaseAuth,
    isSignedIn,
    userData,
    reloadUserData,
  } = useFirebaseAuth();
  const {
    isFromCheckout,
    checkoutSessionId,
    paymentIntentId,
    registerCode,
    setStep,
    posthog,
    isAnonymous,
    subscriptionPlan,
    elementsPaymentResult,
  } = useSubscription();

  const devSuccessPreview = isSubscriptionDevSuccessPreview();
  const alreadyFinalized = !!elementsPaymentResult?.subscriptionId;
  const shouldCheck = !devSuccessPreview && !alreadyFinalized
    && isFromCheckout
    && (!!checkoutSessionId || !!paymentIntentId);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(
    () => shouldCheck,
  );
  const [subscriptionCheckTries, setSubscriptionCheckTries] = useState(SUBSCRIPTION_CHECK_TRIES);
  const [activeSubscription, setActiveSubscription] = useState(
    devSuccessPreview || alreadyFinalized,
  );
  const hasInitiatedPollingRef = useRef(isCheckingSubscription);

  // When finalize already succeeded (elementsPaymentResult is set),
  // skip polling and show success immediately.
  useEffect(() => {
    if (alreadyFinalized && !activeSubscription) {
      setActiveSubscription(true);
      setIsCheckingSubscription(false);
    }
  }, [alreadyFinalized, activeSubscription]);

  // Handle race condition: provider values (paymentIntentId, isFromCheckout)
  // may arrive after mount due to React Router context propagation timing.
  useEffect(() => {
    if (!hasInitiatedPollingRef.current && shouldCheck) {
      hasInitiatedPollingRef.current = true;
      setIsCheckingSubscription(true);
      setSubscriptionCheckTries(SUBSCRIPTION_CHECK_TRIES);
    }
  }, [shouldCheck]);
  const [checkoutSessionStatus, setCheckoutSessionStatus] = useState(null);
  const [checkoutSessionPurchaseData, setCheckoutSessionPurchaseData] = useState({
    amount: null,
    currency: 'USD',
    orderId: checkoutSessionId || paymentIntentId,
    discountCode: '',
  });

  const verifyCheckoutSession = useCallback(async () => {
    const statusFetcher = paymentIntentId
      ? () => getSubscriptionPaymentIntentStatus(paymentIntentId)
      : () => getOrderCheckoutSession(checkoutSessionId);

    const {
      data: checkoutSessionData,
      error: checkoutSessionError,
    } = await statusFetcher();

    if (checkoutSessionError || !checkoutSessionData?.status) {
      setCheckoutSessionStatus(null);
      setCheckoutSessionPurchaseData({
        amount: null,
        currency: 'USD',
        orderId: checkoutSessionId || paymentIntentId,
        discountCode: '',
      });
      setActiveSubscription(false);
      return;
    }

    const purchaseAmount = (
      checkoutSessionData.order?.totalAmount ??
      checkoutSessionData.order?.totalPrice?.amount ??
      checkoutSessionData.order?.amount ??
      checkoutSessionData.amount ??
      0
    );
    const purchaseCurrency = (
      checkoutSessionData.order?.currency ??
      checkoutSessionData.order?.currencyCode ??
      checkoutSessionData.currency ??
      'USD'
    );
    const purchaseOrderId = checkoutSessionData.order?.id ?? checkoutSessionId ?? paymentIntentId;
    const discountApplications = (
      checkoutSessionData.order?.discountApplications ??
      checkoutSessionData.discountApplications ??
      []
    );
    const purchaseDiscountCode = getDiscountCodeFromApplications(discountApplications);

    setCheckoutSessionStatus(checkoutSessionData.status);
    setCheckoutSessionPurchaseData({
      amount: Number(purchaseAmount),
      currency: purchaseCurrency,
      orderId: purchaseOrderId,
      discountCode: purchaseDiscountCode,
    });
    setActiveSubscription(checkoutSessionData.order?.activeSubscription);

    if (checkoutSessionData.order?.activeSubscription && !isLoadingFirebaseAuth && isSignedIn) {
      await reloadUserData();
    }
  }, [checkoutSessionId, paymentIntentId, isSignedIn, isLoadingFirebaseAuth]);

  const handleOnTryAgainClick = useCallback(() => {
    setSubscriptionCheckTries(SUBSCRIPTION_CHECK_TRIES);
    setCheckoutSessionStatus(null);
    setActiveSubscription(false);
    setIsCheckingSubscription(true);
  }, []);

  const handleViewAccount = useCallback(() => {
    posthog.capture(getTrackerIdEventName(trackerClose));
    window.location.assign(TRASHIE_APP_URL);
  }, [posthog]);

  useEffect(() => {
    // In pay-on-reg flows the provider handles verification and redirects
    // back to Register.jsx — skip polling here to avoid duplicate API calls.
    if (registerCode) return undefined;

    if (isCheckingSubscription) {
      if (subscriptionCheckTries > 0) {
        setIsCheckingSubscription(true);
        if (activeSubscription) {
          setIsCheckingSubscription(false);
          const trackProps = {
            value: String(subscriptionPlan?.value ?? 0),
            currency: 'USD',
          };
          window.ttq?.track('Purchase', trackProps);
          trackMagellanPurchase({
            amount: checkoutSessionPurchaseData?.amount ?? Number(subscriptionPlan?.value ?? 0),
            currency: checkoutSessionPurchaseData?.currency ?? 'USD',
            orderId: checkoutSessionPurchaseData?.orderId ?? checkoutSessionId ?? paymentIntentId,
            metadata: buildMagellanPurchaseMetadata('membership', {
              discountCode: checkoutSessionPurchaseData?.discountCode ?? '',
            }),
          });
          const artsAiPurchaseUrl = buildArtsAiPixelUrl({
            action: 'purchase',
            pixelId: ARTSAI_PIXEL_ID,
            value: checkoutSessionPurchaseData?.amount ?? subscriptionPlan?.value ?? 0,
            orderId: checkoutSessionPurchaseData?.orderId ?? checkoutSessionId ?? paymentIntentId ?? '',
            contentId: 'membership',
          });
          const artsAiImg = new Image();
          artsAiImg.src = artsAiPurchaseUrl;
          // Push GTM unlimited_subscription event (only if subscription plan is loaded)
          const subscriptionValue = Number(
            checkoutSessionPurchaseData?.amount ?? subscriptionPlan?.value ?? 0,
          );
          if (subscriptionValue > 0) {
            const originalPrice = Number(subscriptionPlan?.prevValue ?? subscriptionValue);
            const discount = originalPrice > subscriptionValue
              ? originalPrice - subscriptionValue
              : 0;
            pushSubscriptionPurchaseEvent({
              transactionId: checkoutSessionId || paymentIntentId,
              value: subscriptionValue,
              currency: checkoutSessionPurchaseData?.currency ?? 'USD',
              itemName: 'Trashie Unlimited',
              discount,
              userData,
              email: userData?.email,
            });
          }

          clearTimeout(verificationTimeoutRef.current);
        } else if (checkoutSessionStatus === STRIPE_CHECKOUT_SESSION_STATUS.EXPIRED) {
          setIsCheckingSubscription(false);
          clearTimeout(verificationTimeoutRef.current);
        } else {
          verificationTimeoutRef.current = setTimeout(async () => {
            await verifyCheckoutSession();
            setSubscriptionCheckTries(prev => prev - 1);
          }, SUBSCRIPTION_CHECK_TIMEOUT);
        }
      } else {
        setIsCheckingSubscription(false);
        clearTimeout(verificationTimeoutRef.current);
      }
    }

    return () => {
      if (verificationTimeoutRef.current) {
        clearTimeout(verificationTimeoutRef.current);
      }
    };
  }, [
    isCheckingSubscription,
    subscriptionCheckTries,
    activeSubscription,
    checkoutSessionStatus,
    registerCode,
    userData?.uid,
    userData?.email,
    subscriptionPlan?.value,
    subscriptionPlan?.prevValue,
    checkoutSessionId,
    paymentIntentId,
    checkoutSessionPurchaseData,
  ]);

  useEffect(() => {
    if (!isAnonymous && !isLoadingFirebaseAuth && !isSignedIn) {
      setStep(SUBSCRIPTION_STEPS.PAYMENT);
    }
  }, [isLoadingFirebaseAuth, isSignedIn, isAnonymous, setStep]);

  useEffect(() => {
    posthog.capture(getTrackerIdEventName(trackerView));
  }, []);

  if (isCheckingSubscription) {
    return (
      <div ref={confirmationScrollTargetRef} className="SubscriptionConfirmation SubscriptionConfirmation--scrollAnchor">
        <GeneralLottie
          animationData={processingPayment}
          backgroundColor="var(--trashie-ui-faded-black-10)"
        />
      </div>
    );
  }

  if (!isCheckingSubscription && !activeSubscription) {
    return (
      <div ref={confirmationScrollTargetRef} className="SubscriptionConfirmation SubscriptionConfirmation--scrollAnchor">
        <div className="SubscriptionConfirmation__container">
          <img
            className="SubscriptionConfirmation__container-icon"
            src={IconUnplug}
            alt="Unplug"
          />
          <div className="SubscriptionConfirmation__container-title">
            Your payment was successful but our system is having difficulties
            to activate your subscription.
          </div>
          <Button
            type={BUTTON_TYPE.QUATERNARY}
            size={BUTTON_SIZE.LARGE}
            onClick={handleOnTryAgainClick}
            className="SubscriptionConfirmation__container-button"
          >
            Try again
          </Button>
          <div className="SubscriptionConfirmation__container-message">
            Still not working?
          </div>
          <Button
            type={BUTTON_TYPE.LINK_QUINARY}
            onClick={() => window.open(CONTACT_US_URL, '_blank')}
          >
            Contact support
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={confirmationScrollTargetRef}
      className="SubscriptionConfirmation SubscriptionConfirmation--scrollAnchor SubscriptionConfirmation--successV2"
    >
      <header className="SubscriptionConfirmation__successHeaderBar">
        <Link className="SubscriptionConfirmation__successLogoLink" to={ROUTE_HOME}>
          <img
            className="SubscriptionConfirmation__successLogo"
            src={trashieLogo}
            alt="Trashie"
          />
        </Link>
      </header>
      <div className="SubscriptionConfirmation__successContent">
        <h1 className="SubscriptionConfirmation__successHeadline">Congrats!</h1>

        <div className="SubscriptionConfirmation__successCluster">
          <div className="SubscriptionConfirmation__successRule" aria-hidden="true" />
          <div className="SubscriptionConfirmation__successCopy">
            <p className="SubscriptionConfirmation__successCopyLead">
              <span className="SubscriptionConfirmation__successCopyLine1">
                You just unlocked the power of
              </span>
              {' '}
              <span className="SubscriptionConfirmation__successCopyLine2">Trashie Unlimited.</span>
            </p>
            <p className="SubscriptionConfirmation__successCopySpacer" aria-hidden="true">
              &nbsp;
            </p>
            <p>
              Check your inbox for emails from Trashie. Order more bags and boxes anytime in
              your account.
            </p>
          </div>
          <div className="SubscriptionConfirmation__successRule" aria-hidden="true" />
          <div className="SubscriptionConfirmation__successNextBlock">
            <h2 className="SubscriptionConfirmation__successNextTitle">What Happens Next</h2>
            <ol className="SubscriptionConfirmation__successNextList">
              {SUCCESS_NEXT_STEPS.map((line, index) => (
                <li
                  key={line}
                  className="SubscriptionConfirmation__successNextItem"
                >
                  <span
                    className="SubscriptionConfirmation__successNextBadge"
                    aria-hidden="true"
                  >
                    {index + 1}
                  </span>
                  <span className="SubscriptionConfirmation__successNextText">{line}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <div className="SubscriptionConfirmation__successCtaWrap">
          <Button
            type={BUTTON_TYPE.QUATERNARY}
            size={BUTTON_SIZE.LARGE}
            className="SubscriptionConfirmation__successCta"
            onClick={handleViewAccount}
          >
            View My Account →
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionConfirmation;
